import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { getMessageThreads, getMessageThread, sendMessageReply, acceptOffer, getThreadOrder } from '@/lib/api';
import { MessageSquare, Send, ArrowLeft, ClipboardList, CheckCircle, XCircle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'gerade eben';
  if (mins < 60) return `vor ${mins} Min.`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `vor ${hrs} Std.`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `vor ${days} Tagen`;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
};

function ThreadListItem({ thread, isActive, onClick, currentUserId }) {
  const otherName =
    thread.participant_1_id === currentUserId
      ? thread.participant_2_name
      : thread.participant_1_name;
  const initial = (otherName || '?')[0].toUpperCase();

  return (
    <button
      className={`w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border-b ${
        isActive ? 'bg-orange-50 dark:bg-orange-900/15 border-l-2 border-l-orange-500' : ''
      }`}
      onClick={onClick}
      data-testid={`thread-item-${thread.thread_id}`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
            isActive ? 'bg-orange-500' : 'bg-slate-400 dark:bg-slate-600'
          }`}
        >
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span
              className={`text-sm truncate ${
                thread.my_unread > 0 ? 'font-bold' : 'font-medium'
              }`}
            >
              {otherName || 'Unbekannt'}
            </span>
            <span className="text-[10px] text-muted-foreground shrink-0">
              {formatTime(thread.last_message_at)}
            </span>
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {thread.wanted_category && (
              <span className="text-orange-500 font-medium">[{thread.wanted_category}] </span>
            )}
            {thread.wanted_title}
          </p>
          <p className="text-xs text-muted-foreground/70 truncate">{thread.last_message}</p>
        </div>
        {thread.my_unread > 0 && (
          <span className="shrink-0 h-5 min-w-[20px] flex items-center justify-center rounded-full bg-orange-500 text-white text-[10px] font-bold px-1.5">
            {thread.my_unread}
          </span>
        )}
        {thread.offer_status === 'accepted' && (
          <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
        )}
        {thread.offer_status === 'rejected' && (
          <XCircle className="h-4 w-4 text-red-400 shrink-0" />
        )}
      </div>
    </button>
  );
}

function MessageBubble({ message, isOwn }) {
  if (message.message_type === 'system') {
    return (
      <div className="flex justify-center mb-3">
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />
          <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">{message.content}</span>
        </div>
      </div>
    );
  }
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[75%] flex flex-col gap-1 ${isOwn ? 'items-end' : 'items-start'}`}>
        {!isOwn && (
          <span className="text-[10px] text-muted-foreground px-1 font-medium">
            {message.sender_name}
          </span>
        )}
        <div
          className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
            isOwn
              ? 'bg-orange-500 text-white rounded-tr-sm'
              : 'bg-slate-100 dark:bg-slate-700 text-foreground rounded-tl-sm'
          }`}
          data-testid={`message-bubble-${message.message_id}`}
        >
          {message.content}
        </div>
        <span className="text-[10px] text-muted-foreground/60 px-1">
          {formatTime(message.created_at)}
        </span>
      </div>
    </div>
  );
}

export default function MessagesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const hasAutoSelected = useRef(false);

  // Accept offer state
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [acceptForm, setAcceptForm] = useState({ quantity: '', price_per_unit_eur: '', notes: '' });
  const [accepting, setAccepting] = useState(false);
  const [threadOrder, setThreadOrder] = useState(null);

  const loadThreads = useCallback(async () => {
    try {
      const res = await getMessageThreads();
      setThreads(res.data.threads);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) loadThreads();
  }, [user, loadThreads]);

  // Auto-select thread from URL param
  useEffect(() => {
    if (hasAutoSelected.current) return;
    const threadId = searchParams.get('thread');
    if (threadId && threads.length > 0) {
      const t = threads.find((th) => th.thread_id === threadId);
      if (t) {
        hasAutoSelected.current = true;
        handleSelectThread(t);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, searchParams]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSelectThread = async (thread) => {
    setSelectedThread(thread);
    setThreadLoading(true);
    setThreadOrder(null);
    try {
      const res = await getMessageThread(thread.thread_id);
      setMessages(res.data.messages);
      setThreads((prev) =>
        prev.map((t) =>
          t.thread_id === thread.thread_id ? { ...t, my_unread: 0 } : t
        )
      );
      // Load order if thread has been accepted
      if (thread.offer_status === 'accepted' || thread.order_id) {
        try {
          const orderRes = await getThreadOrder(thread.thread_id);
          setThreadOrder(orderRes.data.order);
        } catch { /* no order */ }
      }
    } catch {
      toast.error('Fehler beim Laden der Nachrichten.');
    } finally {
      setThreadLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !selectedThread) return;
    setSending(true);
    const content = reply.trim();
    setReply('');
    try {
      await sendMessageReply(selectedThread.thread_id, content);
      const newMsg = {
        message_id: `tmp-${Date.now()}`,
        thread_id: selectedThread.thread_id,
        sender_id: user.user_id,
        sender_name: user.name || user.email,
        content,
        created_at: new Date().toISOString(),
        read: false,
      };
      setMessages((prev) => [...prev, newMsg]);
      setThreads((prev) =>
        prev.map((t) =>
          t.thread_id === selectedThread.thread_id
            ? { ...t, last_message: content.slice(0, 100), last_message_at: newMsg.created_at }
            : t
        )
      );
    } catch {
      toast.error('Nachricht konnte nicht gesendet werden.');
      setReply(content);
    } finally {
      setSending(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  const computedTotal = (parseFloat(acceptForm.quantity) || 0) * (parseFloat(acceptForm.price_per_unit_eur) || 0);

  const handleAcceptOffer = async () => {
    const qty = parseFloat(acceptForm.quantity);
    const price = parseFloat(acceptForm.price_per_unit_eur);
    if (!qty || qty <= 0) { toast.error('Bitte gültige Menge eingeben'); return; }
    if (price == null || price < 0) { toast.error('Bitte gültigen Preis eingeben'); return; }
    if (computedTotal <= 0) { toast.error('Gesamtbetrag muss größer als 0 sein'); return; }

    setAccepting(true);
    try {
      const res = await acceptOffer(selectedThread.thread_id, {
        quantity: qty,
        price_per_unit_eur: price,
        total_amount_eur: Math.round(computedTotal * 100) / 100,
        notes: acceptForm.notes.trim(),
      });
      toast.success('Angebot erfolgreich angenommen!');
      setAcceptDialogOpen(false);
      setAcceptForm({ quantity: '', price_per_unit_eur: '', notes: '' });

      // Refresh thread
      setSelectedThread((prev) => ({ ...prev, offer_status: 'accepted', order_id: res.data.order_id }));
      setThreads((prev) =>
        prev.map((t) =>
          t.thread_id === selectedThread.thread_id
            ? { ...t, offer_status: 'accepted', order_id: res.data.order_id }
            : t.wanted_id === selectedThread.wanted_id && t.thread_id !== selectedThread.thread_id
            ? { ...t, offer_status: 'rejected' }
            : t
        )
      );
      // Reload messages to show system message
      const msgRes = await getMessageThread(selectedThread.thread_id);
      setMessages(msgRes.data.messages);
      // Load order
      const orderRes = await getThreadOrder(selectedThread.thread_id);
      setThreadOrder(orderRes.data.order);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Annehmen des Angebots');
    } finally {
      setAccepting(false);
    }
  };

  // Determine if the current user is the buyer (wanted ad poster)
  const isBuyer = selectedThread?.participant_1_id === user?.user_id;
  const canAccept = isBuyer && selectedThread && !selectedThread.offer_status;

  const otherParticipantName = selectedThread
    ? selectedThread.participant_1_id === user.user_id
      ? selectedThread.participant_2_name
      : selectedThread.participant_1_name
    : null;

  return (
    <div className="min-h-screen bg-background" data-testid="messages-page">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-6">
          <MessageSquare className="h-6 w-6 text-blue-500" />
          <h1 className="text-2xl font-bold font-manrope">Nachrichten</h1>
          {threads.some((t) => t.my_unread > 0) && (
            <span className="h-6 min-w-[24px] flex items-center justify-center rounded-full bg-orange-500 text-white text-xs font-bold px-2">
              {threads.reduce((s, t) => s + (t.my_unread || 0), 0)}
            </span>
          )}
        </div>

        <div className="flex border rounded-xl overflow-hidden h-[calc(100vh-220px)] min-h-[480px] bg-background shadow-sm">
          {/* ── Thread List ── */}
          <div
            className={`flex flex-col ${
              selectedThread ? 'hidden md:flex' : 'flex'
            } w-full md:w-[300px] lg:w-[320px] border-r shrink-0`}
          >
            <div className="px-4 py-3 border-b bg-slate-50/80 dark:bg-slate-800/50 shrink-0">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Konversationen
                {threads.length > 0 && (
                  <span className="ml-1.5 font-normal normal-case">({threads.length})</span>
                )}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-4 space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex gap-3">
                      <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                      <div className="flex-1 space-y-1.5">
                        <Skeleton className="h-3.5 w-28" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-3/4" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : threads.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-10 text-center px-5">
                  <MessageSquare className="h-10 w-10 text-muted-foreground/25 mb-3" />
                  <p className="text-sm font-medium text-muted-foreground">Keine Nachrichten</p>
                  <p className="text-xs text-muted-foreground/70 mt-1 leading-relaxed">
                    Klicken Sie auf &quot;Anbieten&quot; bei einem Gesuch, um Kontakt aufzunehmen.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-4 gap-1.5"
                    onClick={() => navigate('/wanted')}
                    data-testid="go-to-wanted-btn"
                  >
                    <ClipboardList className="h-4 w-4" />
                    Zu den Gesuchen
                  </Button>
                </div>
              ) : (
                threads.map((thread) => (
                  <ThreadListItem
                    key={thread.thread_id}
                    thread={thread}
                    isActive={selectedThread?.thread_id === thread.thread_id}
                    onClick={() => handleSelectThread(thread)}
                    currentUserId={user.user_id}
                  />
                ))
              )}
            </div>
          </div>

          {/* ── Thread View ── */}
          <div
            className={`flex flex-col flex-1 min-w-0 ${
              !selectedThread ? 'hidden md:flex' : 'flex'
            }`}
          >
            {selectedThread ? (
              <>
                {/* Thread header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b bg-slate-50/80 dark:bg-slate-800/50 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 md:hidden shrink-0"
                    onClick={() => setSelectedThread(null)}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div className="h-9 w-9 rounded-full bg-orange-500 flex items-center justify-center text-white font-semibold text-sm shrink-0">
                    {(otherParticipantName || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm" data-testid="thread-recipient-name">
                      {otherParticipantName}
                    </p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <ClipboardList className="h-3 w-3 text-muted-foreground shrink-0" />
                      <p className="text-xs text-muted-foreground truncate">
                        {selectedThread.wanted_title}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs gap-1.5 shrink-0 hidden sm:flex"
                    onClick={() => navigate('/wanted')}
                  >
                    <ClipboardList className="h-3.5 w-3.5" />
                    Gesuch
                  </Button>
                  {/* Offer status badges & accept button */}
                  {selectedThread.offer_status === 'accepted' && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-semibold shrink-0" data-testid="offer-accepted-badge">
                      <CheckCircle className="h-3.5 w-3.5" />
                      Angenommen
                    </div>
                  )}
                  {selectedThread.offer_status === 'rejected' && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-semibold shrink-0" data-testid="offer-rejected-badge">
                      <XCircle className="h-3.5 w-3.5" />
                      Abgelehnt
                    </div>
                  )}
                  {canAccept && (
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1.5 shrink-0"
                      onClick={() => setAcceptDialogOpen(true)}
                      data-testid="accept-offer-btn"
                    >
                      <CheckCircle className="h-3.5 w-3.5" />
                      Angebot annehmen
                    </Button>
                  )}
                </div>

                {/* Order info banner */}
                {threadOrder && (
                  <div className="mx-4 mt-3 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-800" data-testid="order-info-banner">
                    <div className="flex items-center gap-2 mb-2">
                      <ShieldCheck className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Bestellung #{threadOrder.order_id?.slice(0, 8)}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Menge</span>
                        <p className="font-medium">{threadOrder.quantity} {threadOrder.quantity_unit}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Preis/Einheit</span>
                        <p className="font-medium">{threadOrder.price_per_unit_eur?.toFixed(2)} EUR</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Gesamt</span>
                        <p className="font-semibold text-emerald-700 dark:text-emerald-400">{threadOrder.total_amount_eur?.toFixed(2)} EUR</p>
                      </div>
                    </div>
                    {threadOrder.notes && <p className="text-xs text-muted-foreground mt-1.5 italic">{threadOrder.notes}</p>}
                  </div>
                )}

                {/* Messages area */}
                <div className="flex-1 overflow-y-auto p-4">
                  {threadLoading ? (
                    <div className="space-y-4">
                      {[...Array(5)].map((_, i) => (
                        <div
                          key={i}
                          className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}
                        >
                          <Skeleton className="h-12 w-44 rounded-2xl" />
                        </div>
                      ))}
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <MessageSquare className="h-8 w-8 text-muted-foreground/25 mb-2" />
                      <p className="text-sm text-muted-foreground">Noch keine Nachrichten</p>
                    </div>
                  ) : (
                    <>
                      {messages.map((msg) => (
                        <MessageBubble
                          key={msg.message_id}
                          message={msg}
                          isOwn={msg.sender_id === user.user_id}
                        />
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Reply input */}
                <form
                  onSubmit={handleSend}
                  className="p-3 border-t bg-background flex gap-2 items-end shrink-0"
                >
                  <Textarea
                    placeholder="Nachricht schreiben… (Enter senden, Shift+Enter neue Zeile)"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    className="flex-1 min-h-[56px] max-h-[140px] resize-none text-sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend(e);
                      }
                    }}
                    data-testid="message-reply-input"
                  />
                  <Button
                    type="submit"
                    size="icon"
                    className="bg-orange-500 hover:bg-orange-600 h-10 w-10 shrink-0"
                    disabled={!reply.trim() || sending}
                    data-testid="send-message-btn"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </>
            ) : (
              /* Empty state — no thread selected (desktop) */
              <div className="flex flex-col items-center justify-center h-full text-center px-8">
                <div className="h-16 w-16 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-4">
                  <MessageSquare className="h-8 w-8 text-blue-400" />
                </div>
                <p className="text-lg font-semibold">Kein Gespräch ausgewählt</p>
                <p className="text-sm text-muted-foreground mt-1 max-w-xs leading-relaxed">
                  Wählen Sie eine Konversation aus der linken Liste oder besuchen Sie die Gesuche-Seite.
                </p>
                <Button
                  variant="outline"
                  className="mt-6 gap-2"
                  onClick={() => navigate('/wanted')}
                >
                  <ClipboardList className="h-4 w-4" />
                  Zu den Gesuchen
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Accept Offer Dialog */}
      <Dialog open={acceptDialogOpen} onOpenChange={setAcceptDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="accept-offer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
              Angebot annehmen
            </DialogTitle>
            <DialogDescription>
              Bestätigen Sie die Menge und den Preis, um das Angebot anzunehmen.
              {selectedThread?.wanted_title && (
                <span className="block mt-1 font-medium text-foreground">
                  Gesuch: {selectedThread.wanted_title}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="accept-quantity">Menge</Label>
              <Input
                id="accept-quantity"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="z.B. 500"
                value={acceptForm.quantity}
                onChange={(e) => setAcceptForm((f) => ({ ...f, quantity: e.target.value }))}
                data-testid="accept-quantity-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="accept-price">Preis pro Einheit (EUR)</Label>
              <Input
                id="accept-price"
                type="number"
                step="0.01"
                min="0"
                placeholder="z.B. 2.50"
                value={acceptForm.price_per_unit_eur}
                onChange={(e) => setAcceptForm((f) => ({ ...f, price_per_unit_eur: e.target.value }))}
                data-testid="accept-price-input"
              />
            </div>
            {computedTotal > 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-800">
                <span className="text-sm font-medium">Gesamtbetrag</span>
                <span className="text-lg font-bold text-emerald-700 dark:text-emerald-400" data-testid="accept-total">
                  {computedTotal.toFixed(2)} EUR
                </span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="accept-notes">Anmerkungen (optional)</Label>
              <Textarea
                id="accept-notes"
                placeholder="z.B. Lieferbedingungen, Zeitrahmen..."
                value={acceptForm.notes}
                onChange={(e) => setAcceptForm((f) => ({ ...f, notes: e.target.value }))}
                className="min-h-[60px] resize-none"
                data-testid="accept-notes-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAcceptDialogOpen(false)} data-testid="accept-cancel-btn">
              Abbrechen
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
              onClick={handleAcceptOffer}
              disabled={accepting || computedTotal <= 0}
              data-testid="accept-confirm-btn"
            >
              {accepting ? 'Wird verarbeitet...' : 'Angebot annehmen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
